import React from 'react';
import { Helmet } from 'react-helmet';
import { Tabs } from 'antd';
import { injectIntl } from 'react-intl';
import Authorize from 'components/Providers/Authorize';
import PageWrapper from 'components/Shared/Page/Wrapper';
import PageTitle from 'components/Shared/Page/Title';
import SearchInput from 'components/Shared/Search';
import withPusher from 'components/Providers/Pusher/withPusher';
import PageBanners from 'components/Layout/PageBanners';
import useTranslation from 'utils/hooks/useTranslation';
import FundingRequestsEmptyState from './FundingRequestsEmptyState';
import FundingRequestsTable from './FundingRequestsTable';
import FundingRequestReviewModal from './FundingRequestReviewModal';
import useFundingRequestsPage from './useFundingRequestsPage';

import styles from './style.module.scss';

const { TabPane } = Tabs;

const FUNDING_REQUEST_TABS = ['pending', 'approved', 'rejected', 'all'];

const FUNDING_REQUEST_TAB_LABEL_KEYS = {
  pending: 'corporateAccounts.common.status.pending',
  approved: 'corporateAccounts.fundingRequests.status.approved',
  rejected: 'corporateAccounts.fundingRequests.status.rejected',
  all: 'corporateAccounts.common.all',
};

const FundingRequests = ({ history }) => {
  const { translateText } = useTranslation();
  const {
    searchValue,
    activeTab,
    reviewingRequest,
    requests,
    totalCount,
    pendingCount,
    isLoading,
    currentPage,
    isApproving,
    isDeclining,
    approveError,
    downloadingProofId,
    onSearch,
    onTabChange,
    onReview,
    onCloseReview,
    onPageChange,
    onPageSizeChange,
    onTableChange,
    onApprove,
    onDecline,
    onDownloadProof,
  } = useFundingRequestsPage();

  const searchBar = (
    <SearchInput
      className={styles.searchInput}
      placeholder={translateText('corporateAccounts.fundingRequests.searchPlaceholder')}
      searchValue={searchValue}
      onSearchFunc={onSearch}
    />
  );

  return (
    <Authorize>
      <Helmet title="Funding requests" />
      <PageBanners />
      <PageWrapper history={history} loading={false}>
        <PageTitle
          title={translateText('corporateAccounts.fundingRequests.title')}
          description={translateText('corporateAccounts.fundingRequests.description')}
        />
        <div className="card-body">
          <div className={styles.tabsWrapper}>
            <Tabs
              activeKey={activeTab}
              onChange={onTabChange}
              tabBarExtraContent={searchBar}
              destroyInactiveTabPane
            >
              {FUNDING_REQUEST_TABS.map(tabKey => {
                // Only the Pending tab carries a count (the number of requests
                // awaiting review). It is always present so tab widths stay stable
                // — green when Pending is the open tab, muted otherwise.
                const showCount = tabKey === 'pending' && pendingCount > 0;

                return (
                  <TabPane
                    tab={
                      <span className={styles.tabLabel}>
                        {translateText(FUNDING_REQUEST_TAB_LABEL_KEYS[tabKey])}
                        {showCount && (
                          <span
                            className={`${styles.tabCount} ${
                              activeTab === 'pending' ? styles.tabCountActive : styles.tabCountMuted
                            }`}
                          >
                            {pendingCount}
                          </span>
                        )}
                      </span>
                    }
                    key={tabKey}
                  >
                    {isLoading || totalCount > 0 ? (
                      <FundingRequestsTable
                        status={tabKey}
                        dataSource={requests}
                        loading={isLoading}
                        currentPage={currentPage}
                        totalCount={totalCount}
                        onReview={onReview}
                        onPageChange={onPageChange}
                        onPageSizeChange={onPageSizeChange}
                        onTableChange={onTableChange}
                        onDownloadProof={onDownloadProof}
                        downloadingProofId={downloadingProofId}
                      />
                    ) : (
                      <FundingRequestsEmptyState />
                    )}
                  </TabPane>
                );
              })}
            </Tabs>
          </div>
        </div>
      </PageWrapper>
      <FundingRequestReviewModal
        key={reviewingRequest?.id}
        visible={!!reviewingRequest}
        fundingRequest={reviewingRequest}
        onClose={onCloseReview}
        onApprove={onApprove}
        onDecline={onDecline}
        onDownloadProof={onDownloadProof}
        isApproving={isApproving}
        isDeclining={isDeclining}
        isDownloadingProof={!!downloadingProofId && downloadingProofId === reviewingRequest?.id}
        approveError={approveError}
      />
    </Authorize>
  );
};

export default withPusher(injectIntl(FundingRequests));
